import {
  ExportVideoDetailsViewState,
  ExportVideoDialogState,
} from "./ExportVideoDialog";
import { VideoRenderDto, VideoRenderExportType } from "@repo/api/video-render";
import {
  getTitle as getDownloadTitle,
  getDescription as getDownloadDescription,
  getStatus as getDownloadStatus,
} from "./DownloadDetails";
import {
  getTitle as getVimeoTitle,
  getDescription as getVimeoDescription,
  getStatus as getVimeoStatus,
} from "./VimeoDetails";
import { ExportLinkButton } from "./ExportLinkButton";
import { VideoDto } from "@repo/api/editor";
import AdvertiseVideoFooter from "./AdvertiseVideoFooter";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { ThirdPartyEditorInfoDto } from "@repo/api/third-party-video";
import ExportThirdPartyBack from "./ExportThirdPartyBack";
import { getCompositionDurationMs } from "@repo/video-engine-i2v/utils/timing";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import { DialogHeaderCenter } from "./DialogHeaderCenter";

type Props = {
  video: VideoDto;
  videoRenderExternalId: string;
  dialogState: ExportVideoDetailsViewState;
  setDialogState: (state: ExportVideoDialogState) => void;
  videoRenders: VideoRenderDto[] | undefined;
  exportType: VideoRenderExportType;
};

function getTitle(
  videoRender: VideoRenderDto | null,
  exportType: VideoRenderExportType,
  thirdPartyEditorInfo: ThirdPartyEditorInfoDto | null,
) {
  if (exportType === "DOWNLOAD") {
    return getDownloadTitle(videoRender);
  } else if (exportType === "VIMEO") {
    return getVimeoTitle(videoRender, thirdPartyEditorInfo);
  }
}

function getDescription(
  videoRender: VideoRenderDto | null,
  exportType: VideoRenderExportType,
  thirdPartyEditorInfo: ThirdPartyEditorInfoDto | null,
): string | React.ReactNode {
  if (exportType === "DOWNLOAD") {
    return getDownloadDescription(videoRender);
  } else if (exportType === "VIMEO") {
    return getVimeoDescription(videoRender, thirdPartyEditorInfo);
  }
}

function getStatus(
  videoRender: VideoRenderDto | null,
  exportType: VideoRenderExportType,
): "rendering" | "failed" | "done" | "deleted" {
  if (exportType === "DOWNLOAD") {
    return getDownloadStatus(videoRender);
  } else if (exportType === "VIMEO") {
    return getVimeoStatus(videoRender);
  } else {
    throw new Error("Invalid export type");
  }
}

export default function ExportDetailsView({
  video,
  videoRenderExternalId,
  dialogState,
  setDialogState,
  exportType,
  videoRenders,
}: Props) {
  const thirdPartyEditorInfo = useEditorStore(
    (state) => state.thirdPartyEditorInfo,
  );
  const composition = useEditorStore((state) => state.video?.compositionJson);
  const animatedImages = useEditorStore((state) => state.animatedImages);

  const videoRender =
    videoRenders?.find(
      (render) => render.externalId === videoRenderExternalId,
    ) ?? null;

  if (!videoRender) {
    return (
      <div className="flex h-full flex-1 items-center justify-center">
        <LoadingSpinner size={24} />
      </div>
    );
  }

  const videoDurationMs = composition
    ? getCompositionDurationMs(composition, animatedImages)
    : 0;

  const status = getStatus(videoRender, exportType);
  const tipText =
    status === "failed" && videoRender.upscaleQuality !== null
      ? "Credits have been refunded. Please try again or contact support."
      : 'Tip: You can find this export later in "Export video" → "Earlier exports"';

  return (
    <div className="flex h-full flex-1 flex-col items-center">
      <DialogHeaderCenter
        title={getTitle(videoRender, exportType, thirdPartyEditorInfo)}
        description={getDescription(
          videoRender,
          exportType,
          thirdPartyEditorInfo,
        )}
        onBack={() => setDialogState({ view: dialogState.fromView })}
      />
      <ExportLinkButton
        mode={exportType}
        status={status}
        videoRender={videoRender}
        video={video}
        videoDurationMs={videoDurationMs}
        onShare={
          exportType === "DOWNLOAD"
            ? () =>
                setDialogState({
                  view: "SHARE_LINK",
                  videoRenderExternalId: videoRender.externalId,
                  fromView: "EXPORT_DETAILS",
                  exportType,
                  detailsFromView: dialogState.fromView,
                })
            : undefined
        }
      />
      <p className="mt-1.5 px-8 text-center text-xs text-text-secondary">
        {tipText}
      </p>
      {thirdPartyEditorInfo ? (
        <ExportThirdPartyBack thirdPartyEditorInfo={thirdPartyEditorInfo} />
      ) : (
        <AdvertiseVideoFooter />
      )}
    </div>
  );
}
